import React, { useRef } from 'react';
import { mergeProps, useFocusWithin, useGridList } from 'react-aria';
import { useInView } from 'react-intersection-observer';
import { Item, useListState } from 'react-stately';
import { useMergedRefs } from 'hooks';
import ListItem from './ListItem';
import LoaderRow from './ListItem/LoaderRow';
import * as S from './styles';
import { ListProps } from './types';

/**
 * A component for rendering lists of interactive elements.
 *
 * The structure of the list should be as follows:
 * ```
 *  <List>
 *    <List.Item>item one</List.Item>
 *    <List.Item>item two</List.Item>
 *  </List>
 * ```
 *
 * Alternatively, and possibly easier for dynamic lists:
 * ```
 *  <List items={['one', 'two']}>
 *    {(val) => <List.Item>item {val}</List.Item>}
 *  </List>
 * ```
 *
 * The library used to create the list does not have a great way of exposing
 * state for different rows.  If the row needs to know anything about its state,
 * for example whether or not it is disabled, the `useListItem()` can be used within
 * the component that renders the row.
 *
 * NB: This component was designed for use with Eddy, but there's overlap with
 * Search and some other new design components.  The public API (props) is restricted
 * to just what's needed for now until we better understand how this component will
 * evolve to solve broader use-cases.
 */
function ForwardedList<T extends object>(
  {
    className,
    borderWeight,
    cornerRadius,
    showDivider: showDividerProp = true,
    hasMore = false,
    onLoadMore,
    style,
    threshold = 25,
    variant = 'list',
    ...props
  }: ListProps<T>,
  ref: React.ForwardedRef<HTMLDivElement>,
): React.ReactElement | null {
  const listRef = useRef<HTMLDivElement>(null);

  const state = useListState<T>({
    selectionMode: 'single',
    selectionBehavior: 'toggle',
    ...props,
  });

  const { gridProps } = useGridList<T>(props, state, listRef);

  // detects focus within the list.  if focus is lost, clear the selected item.
  // this means that when navigating with the keyboard, you can press esc to clear
  // the selection or use the mouse to click outside of the list
  const { focusWithinProps } = useFocusWithin({
    onBlurWithin() {
      state.selectionManager.clearSelection();
    },
  });

  const { ref: loaderRef } = useInView({
    rootMargin: `0px 0px ${threshold}px 0px`,
    onChange(inView) {
      if (inView) {
        onLoadMore?.();
      }
    },
  });

  const mergedRefs = useMergedRefs([listRef, ref]);

  const showDivider = variant === 'list' && showDividerProp;

  return (
    <S.Root
      ref={mergedRefs}
      $variant={variant}
      {...{ className, cornerRadius, borderWeight, style }}
      {...mergeProps(gridProps, focusWithinProps)}
    >
      {Array.from(state.collection).map((item) => {
        return <ListItem key={item.key} {...{ showDivider, state, item }} />;
      })}
      {hasMore && (
        <LoaderRow {...{ showDivider, state, variant }} ref={loaderRef} />
      )}
    </S.Root>
  );
}

const ListComponent = React.forwardRef(ForwardedList) as <T extends object>(
  props: ListProps<T> & React.RefAttributes<HTMLDivElement>,
) => React.ReactElement | null;

export const List = Object.assign(ListComponent, {
  Item,
});

export default List;
